import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseDefaultSortOptions {
  defaultSorting?: SortingState;
  syncWithQueryParams?: boolean;
}

const getStringParams = (sorting: SortingState) => {
  return sorting.reduce<string[]>((acc, { id, desc }) => {
    acc.push(`${id}:${desc ? 'desc' : 'asc'}`);

    return acc;
  }, []);
};

const getSortingState = (searchParams: URLSearchParams): SortingState => {
  const sortField = searchParams.get('sortBy');
  const orderDir = searchParams.get('orderDir');

  if (sortField && orderDir) {
    return [
      {
        id: sortField,
        desc: orderDir === 'desc',
      },
    ];
  }

  return [];
};

/*
  applies default sorting when no other sorting is applied
  this sorting is not visible to the user
 */
export const useDefaultSort = (options: UseDefaultSortOptions) => {
  const { defaultSorting = [], syncWithQueryParams } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>(
    syncWithQueryParams ? getSortingState(searchParams) : [],
  );

  useEffect(() => {
    if (syncWithQueryParams) {
      const newSorting = getSortingState(searchParams);

      if (newSorting.length) {
        setSorting(newSorting);
      }
    }
  }, [searchParams, syncWithQueryParams]);

  const setSortingWithParams: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting(updaterOrValue);

    if (syncWithQueryParams) {
      const newSearchParams = new URLSearchParams(searchParams);
      const newSorting =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(sorting)
          : updaterOrValue;

      if (newSorting.length) {
        const { id, desc } = newSorting[0];

        newSearchParams.set('sortBy', id);
        newSearchParams.set('orderDir', desc ? 'desc' : 'asc');
      } else {
        newSearchParams.delete('sortBy');
        newSearchParams.delete('orderDir');
      }

      setSearchParams(newSearchParams);
    }
  };

  const sortingParams = sorting.length ? sorting : defaultSorting;

  const sortingParamsStr = getStringParams(sortingParams);

  return {
    sorting,
    setSorting: setSortingWithParams,
    orderBy: sortingParamsStr.join(','),
  };
};
